/* eslint-disable no-bitwise -- a binary format: flags, attributes and CRC32 are bit fields. */
// Reading ZIP archives in the browser: the table of contents, and one entry at a time through the
// browser's own DecompressionStream. Which entries to take, and what the user is told, is
// prepareFiles' business (render-upload.prepare.ts); this file only knows the format.
//
// Measured on the archives people make (docs/render-landing-port.md, решение 26): Finder and
// Info-ZIP write UTF-8 names without the UTF-8 flag, Finder keeps sizes in the table of contents
// only (data descriptors), and nothing stops a name like '../../x.png'. Free of imports and of
// non-erasable TypeScript so that node runs it as is.

export interface ZipEntry {
  // The name as stored, decoded; see safeZipPath before using it as a path.
  name: string;
  method: number;
  crc32: number;
  compressedSize: number;
  size: number;
  localOffset: number;
  directory: boolean;
  symlink: boolean;
  encrypted: boolean;
}

export interface ZipDirectory {
  entries: ZipEntry[];
  // Bytes in front of the archive proper (a self-extracting stub); local offsets start after it.
  prefix: number;
}

export type ZipFailure =
  | 'damaged'       // no table of contents, offsets outside the file, a stream that breaks
  | 'split'         // one part of a multi-part archive
  | 'too-many'      // more entries than we are willing to list
  | 'encrypted'     // ZipCrypto or AES
  | 'compression'   // a method the browser cannot inflate (bzip2, LZMA, Deflate64, zstd, xz…)
  | 'corrupt-file'  // CRC32 or size mismatch after inflating: `entry` names the file
  | 'too-big'       // inflates past the size the table of contents promised (a ZIP bomb)
  | 'browser'       // no DecompressionStream('deflate-raw')
  | 'memory'        // the device refused the memory
  | 'aborted';      // the caller stopped caring

export class ZipError extends Error {
  public readonly failure: ZipFailure;
  public readonly entry: string;

  constructor(failure: ZipFailure, entry = '') {
    super(entry ? `ZIP ${failure}: ${entry}` : `ZIP ${failure}`);
    this.failure = failure;
    this.entry = entry;
  }
}

const EOCD = 0x06054b50;
const ZIP64_LOCATOR = 0x07064b50;
const ZIP64_EOCD = 0x06064b50;
const CENTRAL = 0x02014b50;
const LOCAL = 0x04034b50;
const STORED = 0;
const DEFLATED = 8;
const AES = 99;
// The end record is 22 bytes after at most 65 535 bytes of comment.
const TAIL_BYTES = 22 + 0xffff;

export async function readZipDirectory(blob: Blob, maxEntries: number): Promise<ZipDirectory> {
  if (blob.size < 22) {
    throw new ZipError('damaged');
  }
  const tailStart = Math.max(0, blob.size - TAIL_BYTES);
  const tail = await bytesOf(blob, tailStart, blob.size - tailStart);
  const view = viewOf(tail);
  let at = -1;
  for (let i = tail.length - 22; i >= 0; i--) {
    if (view.getUint32(i, true) === EOCD && i + 22 + view.getUint16(i + 20, true) <= tail.length) {
      at = i;
      break;
    }
  }
  if (at < 0) {
    throw new ZipError('damaged');
  }
  const eocdPosition = tailStart + at;
  if (view.getUint16(at + 4, true) !== 0 || view.getUint16(at + 6, true) !== 0) {
    throw new ZipError('split');
  }
  let count = view.getUint16(at + 10, true);
  let directorySize = view.getUint32(at + 12, true);
  let directoryOffset = view.getUint32(at + 16, true);
  let directoryEnd = eocdPosition;

  // ZIP64: the real numbers sit in a record the locator right before the end record points at.
  if (count === 0xffff || directorySize === 0xffffffff || directoryOffset === 0xffffffff || hasZip64Locator(view, at)) {
    const locator = hasZip64Locator(view, at) ? viewOf(tail.subarray(at - 20, at)) : null;
    if (!locator) {
      throw new ZipError('damaged');
    }
    if (locator.getUint32(4, true) !== 0) {
      throw new ZipError('split');
    }
    // The record usually sits right before the locator; the locator's own offset is off by any
    // stub in front of the archive, so it is only the second guess.
    let recordPosition = eocdPosition - 20 - 56;
    let record = recordPosition >= 0 ? viewOf(await bytesOf(blob, recordPosition, 56)) : null;
    if (!record || record.getUint32(0, true) !== ZIP64_EOCD) {
      recordPosition = u64(locator, 8);
      record = viewOf(await bytesOf(blob, recordPosition, 56));
      if (record.getUint32(0, true) !== ZIP64_EOCD) {
        throw new ZipError('damaged');
      }
    }
    if (record.getUint32(16, true) !== 0 || record.getUint32(20, true) !== 0) {
      throw new ZipError('split');
    }
    count = u64(record, 32);
    directorySize = u64(record, 40);
    directoryOffset = u64(record, 48);
    directoryEnd = recordPosition;
  }

  if (count > maxEntries) {
    throw new ZipError('too-many', String(count));
  }
  const prefix = directoryEnd - directorySize - directoryOffset;
  if (prefix < 0 || directoryEnd - directorySize < 0) {
    throw new ZipError('damaged');
  }
  const directory = await bytesOf(blob, directoryEnd - directorySize, directorySize);
  const dv = viewOf(directory);
  const entries: ZipEntry[] = [];
  let p = 0;
  for (let i = 0; i < count; i++) {
    if (p + 46 > directory.length || dv.getUint32(p, true) !== CENTRAL) {
      throw new ZipError('damaged');
    }
    const madeBy = dv.getUint16(p + 4, true);
    const flags = dv.getUint16(p + 8, true);
    const method = dv.getUint16(p + 10, true);
    const crc32 = dv.getUint32(p + 16, true);
    let compressedSize = dv.getUint32(p + 20, true);
    let size = dv.getUint32(p + 24, true);
    const nameLength = dv.getUint16(p + 28, true);
    const extraLength = dv.getUint16(p + 30, true);
    const commentLength = dv.getUint16(p + 32, true);
    const external = dv.getUint32(p + 38, true);
    let localOffset = dv.getUint32(p + 42, true);
    const rawName = directory.subarray(p + 46, p + 46 + nameLength);
    const extra = directory.subarray(p + 46 + nameLength, p + 46 + nameLength + extraLength);
    if (p + 46 + nameLength + extraLength + commentLength > directory.length) {
      throw new ZipError('damaged');
    }

    let unicodeName = '';
    for (let e = 0; e + 4 <= extra.length;) {
      const ev = viewOf(extra.subarray(e));
      const id = ev.getUint16(0, true);
      const length = ev.getUint16(2, true);
      const body = viewOf(extra.subarray(e + 4, e + 4 + length));
      if (id === 0x0001) {
        // Only the fields that overflowed are here, in this order.
        let f = 0;
        if (size === 0xffffffff && f + 8 <= length) { size = u64(body, f); f += 8; }
        if (compressedSize === 0xffffffff && f + 8 <= length) { compressedSize = u64(body, f); f += 8; }
        if (localOffset === 0xffffffff && f + 8 <= length) { localOffset = u64(body, f); f += 8; }
      } else if (id === 0x7075 && length > 5 && body.getUint8(0) === 1 && body.getUint32(1, true) === crc32Of(rawName)) {
        // Info-ZIP Unicode Path, written by 7-Zip and WinRAR next to a name in a DOS code page.
        unicodeName = strictUtf8(extra.subarray(e + 9, e + 4 + length)) || '';
      }
      e += 4 + length;
    }

    const name = unicodeName || decodeName(rawName, (flags & 0x0800) !== 0);
    const unixMode = (madeBy >> 8) === 3 ? external >>> 16 : 0;
    entries.push({
      name,
      method,
      crc32,
      compressedSize,
      size,
      localOffset,
      directory: /[\\/]$/.test(name),
      symlink: (unixMode & 0o170000) === 0o120000,
      encrypted: (flags & 0x0001) !== 0 || method === AES,
    });
    p += 46 + nameLength + extraLength + commentLength;
  }
  return { entries, prefix };
}

// The Compression Streams API is newer than this project's TypeScript DOM typings.
type InflateStream = new (format: 'deflate-raw') => TransformStream<Uint8Array, Uint8Array>;

function inflateStream(): InflateStream | undefined {
  return (globalThis as unknown as { DecompressionStream?: InflateStream }).DecompressionStream;
}

// Chrome and Edge 103, Safari 16.4, Firefox 113; older ones throw on 'deflate-raw'.
export function canInflate(): boolean {
  const Inflate = inflateStream();
  try {
    return !!Inflate && !!new Inflate('deflate-raw');
  } catch {
    return false;
  }
}

export function isSupportedMethod(entry: ZipEntry): boolean {
  return entry.method === STORED || entry.method === DEFLATED;
}

// The entry's bytes, checked against the size and CRC32 the table of contents promised.
// `onBytes` hears every chunk; `shouldStop` is asked between chunks.
export async function extractZipEntry(
  blob: Blob,
  directory: ZipDirectory,
  entry: ZipEntry,
  onBytes?: (bytes: number) => void,
  shouldStop?: () => boolean,
): Promise<Blob> {
  if (entry.encrypted) {
    throw new ZipError('encrypted', entry.name);
  }
  if (!isSupportedMethod(entry)) {
    throw new ZipError('compression', entry.name);
  }
  if (entry.method === DEFLATED && !canInflate()) {
    throw new ZipError('browser', entry.name);
  }
  const headerAt = directory.prefix + entry.localOffset;
  if (headerAt + 30 > blob.size) {
    throw new ZipError('damaged', entry.name);
  }
  const header = viewOf(await bytesOf(blob, headerAt, 30));
  if (header.getUint32(0, true) !== LOCAL) {
    throw new ZipError('damaged', entry.name);
  }
  // The local header carries its own name and extra field, not always as long as the central one.
  const dataAt = headerAt + 30 + header.getUint16(26, true) + header.getUint16(28, true);
  if (dataAt + entry.compressedSize > blob.size) {
    throw new ZipError('damaged', entry.name);
  }
  const raw = blob.slice(dataAt, dataAt + entry.compressedSize);
  if (entry.method === STORED && entry.compressedSize !== entry.size) {
    throw new ZipError('damaged', entry.name);
  }

  let stream: ReadableStream<Uint8Array> = raw.stream();
  const Inflate = inflateStream();
  if (entry.method === DEFLATED && Inflate) {
    stream = stream.pipeThrough(new Inflate('deflate-raw'));
  }
  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];
  let crc = 0xffffffff;
  let total = 0;
  try {
    for (;;) {
      if (shouldStop?.()) {
        throw new ZipError('aborted');
      }
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      total += value.length;
      if (total > entry.size) {
        throw new ZipError('too-big', entry.name);
      }
      crc = crc32Update(crc, value);
      if (entry.method === DEFLATED) {
        chunks.push(value);
      }
      onBytes?.(value.length);
    }
  } catch (error) {
    reader.cancel().catch(() => undefined);
    throw asZipError(error, entry.name);
  }
  if (total !== entry.size || (crc ^ 0xffffffff) >>> 0 !== entry.crc32) {
    throw new ZipError('corrupt-file', entry.name);
  }
  try {
    // A stored entry is the slice itself: nothing is copied into memory.
    return entry.method === STORED ? raw : new Blob(chunks);
  } catch (error) {
    throw asZipError(error, entry.name);
  }
}

// A name from an archive as a path we are willing to upload under: forward slashes, no drive, no
// leading slash, no '.' and no '..'. null when the name climbs out of the archive or is empty.
export function safeZipPath(name: string): string | null {
  const parts = name.replace(/\\/g, '/').replace(/^[a-zA-Z]:/, '').split('/');
  const out: string[] = [];
  for (const raw of parts) {
    const part = raw.replace(/[\u0000-\u001f\u007f]/g, '').trim();
    if (!part || part === '.') {
      continue;
    }
    if (part === '..') {
      if (out.length === 0) {
        return null;
      }
      out.pop();
      continue;
    }
    out.push(part);
  }
  return out.length ? out.join('/').normalize('NFC') : null;
}

// --- names ---

// UTF-8 when flagged, and also when the bytes simply are UTF-8: Finder and Info-ZIP never set the
// flag. Anything else is taken as DOS code page 437, which the format prescribes.
function decodeName(bytes: Uint8Array, flaggedUtf8: boolean): string {
  if (flaggedUtf8) {
    return new TextDecoder('utf-8').decode(bytes);
  }
  const utf8 = strictUtf8(bytes);
  if (utf8 !== null) {
    return utf8;
  }
  let out = '';
  for (const byte of bytes) {
    out += byte < 0x80 ? String.fromCharCode(byte) : CP437_HIGH[byte - 0x80];
  }
  return out;
}

function strictUtf8(bytes: Uint8Array): string | null {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
}

const CP437_HIGH =
  'ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜ¢£¥₧ƒáíóúñÑªº¿⌐¬½¼¡«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ ';

// --- plumbing ---

function hasZip64Locator(tail: DataView, eocdAt: number): boolean {
  return eocdAt >= 20 && tail.getUint32(eocdAt - 20, true) === ZIP64_LOCATOR;
}

function asZipError(error: unknown, entry: string): ZipError {
  if (error instanceof ZipError) {
    return error;
  }
  const name = (error as { name?: string } | null)?.name;
  if (error instanceof RangeError || name === 'QuotaExceededError' || name === 'NotReadableError') {
    return new ZipError('memory', entry);
  }
  // DecompressionStream reports a broken deflate stream as a TypeError.
  return new ZipError('damaged', entry);
}

async function bytesOf(blob: Blob, offset: number, length: number): Promise<Uint8Array> {
  if (offset < 0 || offset + length > blob.size) {
    throw new ZipError('damaged');
  }
  try {
    return new Uint8Array(await blob.slice(offset, offset + length).arrayBuffer());
  } catch (error) {
    throw asZipError(error, '');
  }
}

function viewOf(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}

function u64(view: DataView, offset: number): number {
  return view.getUint32(offset, true) + view.getUint32(offset + 4, true) * 0x100000000;
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32Update(crc: number, bytes: Uint8Array): number {
  let c = crc;
  for (const byte of bytes) {
    c = CRC_TABLE[(c ^ byte) & 0xff] ^ (c >>> 8);
  }
  return c;
}

function crc32Of(bytes: Uint8Array): number {
  return (crc32Update(0xffffffff, bytes) ^ 0xffffffff) >>> 0;
}
